// Solo-mode shared types + Zod schemas. PR 1 shipped the
// player-creation shapes; PR 3 adds SoloSessionState, the request
// schemas for the 6 non-battle endpoints, and the response envelopes.
// /combine + /ready land in PR 4.
import { z } from 'zod';
import { GlyphKeySchema } from './units.js';
// The icon picker is a curated subset of the full glyph catalog —
// these read clearly as small mascot stamps next to a player's name.
// Add to this list as new compelling mascot glyphs ship; the union
// stays a strict subset of GlyphKeySchema so the schema can never
// drift past the catalog.
export const PLAYER_ICON_KEYS = [
    'crane',
    'frog',
    'tiger',
    'fox',
    'pup',
    'owl',
    'koi',
    'wolf',
    'phoenix',
    'mantis',
    'mouse',
    'beetle',
];
export const PlayerIconSchema = z.enum(PLAYER_ICON_KEYS);
// Sanity-check at module load that every icon is a valid glyph key.
// If a name is removed from GlyphKeySchema upstream the enum.parse
// here throws at boot.
PLAYER_ICON_KEYS.forEach((k) => GlyphKeySchema.parse(k));
const TrimmedString = (min, max) => z
    .string()
    .transform((s) => s.trim())
    .pipe(z.string().min(min).max(max));
export const CreatePlayerSchema = z.object({
    display_name: TrimmedString(1, 24),
    team_name: TrimmedString(1, 24),
    icon: PlayerIconSchema,
});
// Error envelope shared across all solo / player endpoints. Each
// route's failure path returns { error: { code, message? } } with the
// code from this enum.
export const PlayerErrorCodes = ['no_session', 'invalid_body'];
export const PlayerErrorCodeSchema = z.enum(PlayerErrorCodes);
// Shop-action error codes. Returned by the pure try* functions in the
// server-side shop state machine and surfaced to clients by the solo
// route handlers (PR 3+). Kept in shared so client error-handling can
// branch on a typed enum rather than string-matching.
export const SoloErrorCodes = [
    'invalid_slot_index',
    'slot_already_bought',
    'insufficient_gold',
    'team_full',
    'unit_not_found',
    'cannot_combine',
    'cannot_reorder',
    'match_not_found',
    'match_finished',
    'wrong_phase',
    'empty_team',
];
export const SoloErrorCodeSchema = z.enum(SoloErrorCodes);
/** Friendly one-line phrasing for any shop-action failure code the
 *  client wants to surface as a toast. Unknown codes pass through
 *  verbatim so we never silently drop information. */
export function friendlyShopErrorMessage(code) {
    switch (code) {
        case 'insufficient_gold':
            return 'Not enough gold for this card.';
        case 'cannot_combine':
            return "These pieces don't combine.";
        case 'team_full':
            return 'No room on your team.';
        case 'slot_already_bought':
            return 'That shop slot is already empty.';
        case 'cannot_reorder':
            return "Can't move that piece there.";
        case 'invalid_slot_index':
            return 'That slot no longer exists.';
        case 'unit_not_found':
            return 'That unit is gone.';
        case 'wrong_phase':
            return 'Not available right now.';
        case 'empty_team':
            return 'Your team is empty — add a unit first.';
        default:
            return code.replace(/_/g, ' ');
    }
}
/** Action-failure codes that should NOT flip the client to a hard
 *  error phase — they're recoverable mid-shop mistakes the player
 *  can react to (toast). Session-fatal codes (no_session, etc.) take
 *  the existing phase-flip path. */
export const TRANSIENT_SHOP_ERROR_CODES = new Set([
    'insufficient_gold',
    'cannot_combine',
    'team_full',
    'slot_already_bought',
    'cannot_reorder',
    'invalid_slot_index',
    'unit_not_found',
    'wrong_phase',
    'empty_team',
]);
// =============================================================
//  Solo session state (PR 3)
// =============================================================
// What the server sends back from every solo endpoint. Contains
// everything a client needs to render the next frame:
//   - identity bits (match_id, phase, round)
//   - the ShopState the UI already renders
//   - the player's run-level stats (wins, health)
//   - the seed string (used by the replay screen in PR 4 to seed the
//     animation timeline so re-watches stay deterministic)
export const SoloPhases = ['shop', 'battle', 'done'];
export const SoloPhaseSchema = z.enum(SoloPhases);
// =============================================================
//  Request schemas — one per non-battle endpoint
// =============================================================
// Active line is 5 slots (positions 0..4). Bench is 3 (positions 0..2).
// CombineLocation references these.
export const BoardWhereSchema = z.enum(['active', 'bench']);
export const BoardPositionSchema = z.number().int().min(0).max(4);
export const BoardLocationSchema = z.object({
    where: BoardWhereSchema,
    position: BoardPositionSchema,
});
// /start — empty body (player from cookie).
export const StartSoloSchema = z.object({}).passthrough();
export const BuySchema = z.object({
    slot_index: z.number().int().min(0).max(20),
    /** Optional drop target. When the target is occupied by a
     *  same-unit-same-star copy, the buy folds the fresh ★1 into that
     *  unit atomically — no empty slot required (handy when team is at
     *  5+3 full but a combine is still valid). When omitted, server
     *  places the bought unit in the first empty active slot. */
    target: BoardLocationSchema.optional(),
});
export const SellSchema = z.object({
    where: BoardWhereSchema,
    position: BoardPositionSchema,
});
export const RerollSchema = z.object({}).passthrough();
export const LockSchema = z.object({
    slot_index: z.number().int().min(0).max(20),
});
export const ReorderSchema = z.object({
    from: BoardLocationSchema,
    to: BoardLocationSchema,
});
export const CombineSchema = z.object({
    from: BoardLocationSchema,
    to: BoardLocationSchema,
});
export const ReadySchema = z.object({}).passthrough();
//# sourceMappingURL=solo.js.map